import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from '@/api';
import { FilterType } from '@/enums';
import {
    DataTableExportParams,
    DistrictProcessingInProgress,
    DistrictProcessingStatistic,
    DistrictProcessingStatisticOptions,
    DistrictProcessingStatisticSummary,
    PaginatedResponse
} from '@/interfaces';

@Injectable({
    providedIn: 'root'
})
export class DistrictProcessingStatisticsService {

    constructor(private apiService: ApiService) {
    }

    listing(params: Record<string, any>): Observable<PaginatedResponse<DistrictProcessingStatistic>> {
        return this.apiService.get<any>('/district-processing-statistics', this.apiService.build(params)).pipe(
            map((res: any) => ({
                data: res.data,
                total: res.total,
                current_page: res.current_page,
                per_page: res.per_page
            }))
        );
    }

    options(context: Record<string, any> = {}): Observable<DistrictProcessingStatisticOptions> {
        return this.apiService.get<DistrictProcessingStatisticOptions>(
            '/district-processing-statistics/options',
            this.apiService.build(context)
        ).pipe(
            map(res => res.data ?? res)
        );
    }

    summary(params: Record<string, any> = {}): Observable<DistrictProcessingStatisticSummary> {
        return this.apiService.get<DistrictProcessingStatisticSummary>(
            '/district-processing-statistics/summary',
            this.apiService.build(params)
        ).pipe(map((res: any) => res.data ?? res));
    }

    inProgress(params: Record<string, any> = {}): Observable<DistrictProcessingInProgress> {
        return this.apiService.get<DistrictProcessingInProgress>(
            '/district-processing-statistics/in-progress',
            this.apiService.build(params)
        ).pipe(map((res: any) => res.data ?? res));
    }

    export(params: DataTableExportParams): Observable<Blob> {
        return this.apiService.postBlob('/district-processing-statistics/export', params);
    }

    rowForStateYear(state: string, year: number): Observable<DistrictProcessingStatistic | null> {
        return this.listing({
            'filter[state][type]': FilterType.MULTISELECT,
            'filter[state][value][0]': state,
            'filter[year][type]': FilterType.MULTISELECT,
            'filter[year][value][0]': year,
            page: 1,
            per_page: 1
        }).pipe(
            map((res) =>
                res.data.find((row) => row.state === state && row.year === year) ?? res.data[0] ?? null
            )
        );
    }

    rowsForState(state: string): Observable<DistrictProcessingStatistic[]> {
        return this.listing({
            'filter[state][type]': FilterType.MULTISELECT,
            'filter[state][value][0]': state,
            page: 1,
            per_page: 100,
            sort_field: 'year',
            sort_order: 'asc'
        }).pipe(
            map((res) =>
                res.data
                    .filter((row) => row.state === state)
                    .sort((a, b) => a.year - b.year)
            )
        );
    }
}
